
import { Track } from './model/Track';
import { memo, useEffect, useRef, useState } from 'react';
import YouTube, { YouTubeProps, YouTubePlayer, YouTubeEvent } from 'react-youtube';
import { logPrefix } from './util/Log';

export type TubeMethods = {
  playTrack: (track: Track, quickly?: boolean) => void;
  play: () => void;
  pause: () => void;
};

type TubeProps = {
  onMethodsReady?: (methods: TubeMethods) => void,
  onTrackEnd?: () => void,
  onPlay?: () => void,
  onPause?: () => void,
};

export default memo(function Tube(
  { onMethodsReady, onTrackEnd, onPlay, onPause }: TubeProps) {

  const playerRef = useRef<YouTubePlayer | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRef = useRef<boolean>(true);

  function playTrack(track: Track, quickly = true) {
    const videoId = track.videoId;
    const startSeconds = track.startSeconds;
    const endSeconds = track.endSeconds;

    if (quickly) {
      console.log(logPrefix(), 'player.loadVideoById()', { videoId, startSeconds, endSeconds });
      playerRef.current!.loadVideoById({ videoId, startSeconds, endSeconds });
    } else {
      console.log(logPrefix(), 'player.cueVideoById()', { videoId, startSeconds, endSeconds });
      playerRef.current!.cueVideoById({ videoId, startSeconds, endSeconds });
    }
  }

  function play() {
    console.log(logPrefix(), 'player.playVideo()');
    playerRef.current!.playVideo();
  }

  function pause() {
    console.log(logPrefix(), 'player.pauseVideo()');
    playerRef.current!.pauseVideo()
    if (timerIdRef.current) {
      clearTimeout(timerIdRef.current);
    }
  }

  function stop() {
    console.log(logPrefix(), 'player.stopVideo()');
    playerRef.current!.stopVideo();
  }

  ///////////////////// end of function

  useEffect(() => {
    return () => {
      console.log(logPrefix(), 'player.destroy()');
      playerRef.current?.destroy();
      playerRef.current = null;
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
    }
  }, []);
  ///////////////////// end of useEffect

  console.log(logPrefix(), 'Tube render');
  return (<>
    <YouTube
      className='tubePlayer'
      opts={{
        height: '360',
        width: '640',
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          showinfo: 1,
          enablejsapi: 1
        },
      }}

      onReady={(event: YouTubeEvent) => {
        playerRef.current = event.target;
        onMethodsReady?.({ playTrack, play, pause });
      }}

      onPlay={(event: YouTubeEvent) => {
        isPlayingRef.current = true;
        onPlay?.();
      }}

      onPause={(event: YouTubeEvent) => {
        isPlayingRef.current = false;
        onPause?.();
      }}

      onEnd={(event: YouTubeEvent) => {
        // なぜか、UNSTARTED->ENDEDの遷移があるので開始したことを前提とする
        if (isPlayingRef.current) {
          onTrackEnd?.();
          isPlayingRef.current = false;
        }
      }}

      onError={(error: YouTubeEvent<number>) => {
        const msg = 'YouTube Player Error:' + error.data;
        console.error(logPrefix(), msg);
        alert(msg);
      }}

      onStateChange={(ev: YouTubeEvent<number>) => {
        console.debug(logPrefix(), `onStateChange statu=${toStatus(ev.data)}`);

        if (ev.data === YT.PlayerState.CUED) {

          // 初回は自動再生しない ユーザアクションがないと再生できないケースがあるので
          if (isFirstRef.current) {
            isFirstRef.current = false;
            return;
          }

          timerIdRef.current = setTimeout(() => {
            console.log(logPrefix(), 'player.playVideo() by timer');
            playerRef.current.playVideo();
            timerIdRef.current = null;
          }, 4000);
        }
      }}
    />
  </>);
})
/////////////////////////////////////////////////////////////////////////////


function toStatus(statusCode: number): string {
  switch (statusCode) {
    case YT.PlayerState.UNSTARTED:
      return 'UNSTARTED';
    case YT.PlayerState.ENDED:
      return 'ENDED';
    case YT.PlayerState.PLAYING:
      return 'PLAYING';
    case YT.PlayerState.PAUSED:
      return 'PAUSED';
    case YT.PlayerState.BUFFERING:
      return 'BUFFERING';
    case YT.PlayerState.CUED:
      return 'CUED';
    default:
      return '不明な状態';
  }
}
